import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "motion/react";
import {
  ArrowRight,
  Star,
  TrendingUp,
  DollarSign,
  Clock,
  Users,
  Zap,
  Target,
  ShieldCheck,
  ChevronDown,
  AlertCircle,
  Flame
} from "lucide-react";
import logo from "./public/logo.png";

function getHeadlineFromURL(): string {
  const params = new URLSearchParams(window.location.search);
  const keyword = params.get("h") || params.get("headline") || params.get("kw");
  if (keyword) {
    return decodeURIComponent(keyword);
  }
  return "";
}

// Build apply URL preserving UTM params
function getApplyUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const applyParams = new URLSearchParams();

  // Preserve tracking parameters
  if (params.get("utm_source")) applyParams.set("utm_source", params.get("utm_source")!);
  if (params.get("source")) applyParams.set("source", params.get("source")!);
  if (params.get("h")) applyParams.set("h", params.get("h")!);
  if (params.get("headline")) applyParams.set("headline", params.get("headline")!);

  const queryString = applyParams.toString();
  return queryString ? `/apply?${queryString}` : "/apply";
}

// Animated counter
function AnimatedNumber({ value, prefix = "", suffix = "" }: { value: number; prefix?: string; suffix?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000;
      const increment = value / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setDisplayValue(value);
          clearInterval(timer);
        } else {
          setDisplayValue(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
}

// Section reveal animation
function RevealSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const painPoints = [
  "Stuck at a dead-end job while your potential goes to waste",
  "Watching others your age buy cars, travel, and stack money",
  "Living paycheck to paycheck in your 20s with no clear path forward",
  "Trading your best years for minimum wage with nothing to show for it",
  "Feeling like you're falling behind while life passes you by"
];

const transformations = [
  { before: "Making $15/hr at a job going nowhere", after: "Earning $8K-$15K/month before you're 25" },
  { before: "No degree = no opportunities", after: "Fully trained in 2 weeks, selling in week 3" },
  { before: "Working hard but staying broke", after: "Your hustle directly equals your paycheck" },
  { before: "Watching your 20s slip away", after: "Building real wealth while you're young" }
];

const whatYouGet = [
  { icon: Target, title: "Exclusive Leads Provided", desc: "We give you people who WANT to buy. No cold calling randoms." },
  { icon: Zap, title: "Complete Sales Training", desc: "Scripts, objection handling, closing techniques. We teach you everything." },
  { icon: Users, title: "1-on-1 Mentorship", desc: "Personal coaching from agents making $20K+/month." },
  { icon: ShieldCheck, title: "Licensing Support", desc: "We help you get licensed fast. Many pass in under a week." },
  { icon: DollarSign, title: "Uncapped Commissions", desc: "No ceiling. Top performers earn $200K+/year." },
  { icon: Clock, title: "Fast Start Bonus", desc: "Hit targets in your first 30 days? Extra cash in your pocket." }
];

const testimonials = [
  {
    quote: "I was making $2,400/month as a server. Last month I made $11,200. Same hours, completely different life.",
    name: "Marcus T.",
    age: 24,
    role: "Former Restaurant Server",
    income: "$11,200/mo",
    image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400"
  },
  {
    quote: "No degree, no connections. Just followed the training. 6 months in, I out-earn my friends with corporate jobs.",
    name: "Jasmine R.",
    age: 23,
    role: "No Prior Sales Experience",
    income: "$9,800/mo",
    image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400"
  },
  {
    quote: "My parents said sales wasn't a 'real career.' Now I make more than both of them combined. Mindset shift.",
    name: "Devon W.",
    age: 26,
    role: "College Dropout",
    income: "$14,500/mo",
    image: "https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=400"
  }
];

const faqs = [
  {
    q: "Who is this opportunity for?",
    a: "We're specifically looking for young, ambitious people who are hungry to build something. Our best performers are typically in their early-to-mid 20s with zero sales experience but tons of drive. If you're coachable, competitive, and tired of settling — this is for you."
  },
  {
    q: "Do I need experience?",
    a: "No. Zero experience required. We've taken people from retail, food service, Uber drivers, students — and turned them into top earners. Our training system works."
  },
  {
    q: "Is this commission-only?",
    a: "Yes. And that's a GOOD thing. Commission-only means uncapped income. Salaried jobs pay you the same whether you work hard or slack off. Here, your effort directly equals your income."
  },
  {
    q: "How fast can I start making money?",
    a: "Most agents make their first sale within 2-3 weeks. Some hit $5K+ in their first full month. It depends on how fast you move through training and start taking calls."
  },
  {
    q: "What's the catch?",
    a: "The catch is it takes work. This isn't a get-rich-quick scheme. It's a real skill that pays extremely well if you're willing to learn and put in the reps."
  },
  {
    q: "Do I need to be licensed?",
    a: "Yes, but we help you get licensed. It's a simple online course and exam. Most people pass in 3-7 days. We cover the study materials."
  }
];

export function App() {
  const [urlHeadline] = useState(getHeadlineFromURL());
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const applyUrl = getApplyUrl();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Floating CTA */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-linear-to-t from-black via-black/90 to-transparent md:hidden"
      >
        <a
          href={applyUrl}
          className="w-full py-4 bg-linear-to-r from-emerald-500 to-emerald-600 text-white font-bold text-lg rounded-xl flex items-center justify-center gap-2"
        >
          Apply Now — Limited Spots <ArrowRight className="w-5 h-5" />
        </a>
      </motion.div>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-svh flex flex-col">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(59,130,246,0.1),transparent_60%)]" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
        </div>

        {/* Nav */}
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative z-20 flex items-center justify-between px-5 md:px-10 lg:px-16 py-5"
        >
          <img src={logo} alt="Empire Insurance" className="h-10 md:h-12" />
          <div className="hidden md:flex items-center gap-3">
            <span className="text-emerald-400 text-sm font-medium flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              12 spots left in South FL
            </span>
            <a
              href={applyUrl}
              className="bg-white text-black text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Apply Now
            </a>
          </div>
        </motion.nav>

        {/* Hero Content */}
        <motion.div
          className="relative z-10 flex-1 flex items-center px-5 md:px-10 lg:px-16 pb-24 md:pb-10"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <div className="w-full max-w-6xl mx-auto">
            <div className="max-w-3xl">
              {/* Eyebrow */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap items-center gap-2 mb-6"
              >
                <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium">
                  🔥 Now Hiring Young Talent
                </span>
                <span className="px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
                  🌴 South Florida
                </span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6"
              >
                {urlHeadline ? (
                  <>
                    <span className="text-white">{urlHeadline}</span>
                  </>
                ) : (
                  <>
                    Make up to <span className="text-transparent bg-clip-text bg-linear-to-r from-emerald-400 to-emerald-500">$8K-$15K/Month</span>
                    <br />
                    <span className="text-white">Selling Health Insurance</span>
                  </>
                )}
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="text-lg md:text-xl text-gray-400 mb-8 leading-relaxed max-w-xl"
              >
                We're building a team of <span className="text-white font-semibold">young, hungry, ambitious</span> salespeople in South Florida.
                No experience needed — just drive and coachability. We'll teach you the rest.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-10"
              >
                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href={applyUrl}
                  className="group inline-flex items-center justify-center gap-2 bg-linear-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-lg font-bold px-8 py-4 rounded-xl transition-all shadow-[0_0_30px_rgba(16,185,129,0.3)]"
                >
                  Start Your Application
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
                <a
                  href="#proof"
                  className="inline-flex items-center justify-center gap-2 text-gray-400 hover:text-white text-lg font-medium px-6 py-4 transition-colors"
                >
                  See Results First
                  <ChevronDown className="w-5 h-5" />
                </a>
              </motion.div>

              {/* Social Proof Bar */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="flex flex-wrap items-center gap-6 text-sm text-gray-500"
              >
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <img src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100" alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#0a0a0a]" />
                    <img src="https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100" alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#0a0a0a]" />
                    <img src="https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=100" alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#0a0a0a]" />
                    <img src="https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100" alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#0a0a0a]" />
                    <img src="https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=100" alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#0a0a0a]" />
                  </div>
                  <span>500+ agents hired</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                  <span className="ml-1">4.9/5 rating</span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="relative z-10 border-t border-white/10 bg-black/50 backdrop-blur-sm"
        >
          <div className="max-w-6xl mx-auto px-5 md:px-10 lg:px-16 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  <AnimatedNumber value={8500} prefix="$" />
                </div>
                <div className="text-sm text-gray-500">Avg. Monthly Income</div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  <AnimatedNumber value={14} suffix=" Days" />
                </div>
                <div className="text-sm text-gray-500">To First Sale</div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  <AnimatedNumber value={94} suffix="%" />
                </div>
                <div className="text-sm text-gray-500">Success Rate</div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-2xl md:text-3xl font-bold text-emerald-400">
                  <AnimatedNumber value={0} prefix="$" />
                </div>
                <div className="text-sm text-gray-500">Cost to Start</div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Pain Section */}
      <section className="relative px-5 md:px-10 lg:px-16 py-20 md:py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <RevealSection>
            <div className="text-center mb-12">
              <span className="text-red-400 text-sm font-semibold tracking-wide uppercase mb-3 block">
                Sound Familiar?
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4">
                You're Not Broke.<br />
                <span className="text-gray-500">You're Just in the Wrong Vehicle.</span>
              </h2>
            </div>
          </RevealSection>

          <div className="space-y-4">
            {painPoints.map((point, i) => (
              <RevealSection key={i} delay={i * 0.1}>
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-0.5">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  </div>
                  <p className="text-gray-300 text-lg">{point}</p>
                </div>
              </RevealSection>
            ))}
          </div>

          <RevealSection delay={0.5}>
            <div className="mt-12 text-center">
              <p className="text-xl md:text-2xl text-white font-semibold mb-2">
                What if there was a way out?
              </p>
              <p className="text-gray-500">
                A skill that actually pays what you're worth?
              </p>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="relative px-5 md:px-10 lg:px-16 py-20 md:py-28 bg-linear-to-b from-emerald-950/30 to-transparent border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="text-emerald-400 text-sm font-semibold tracking-wide uppercase mb-3 block">
                The Transformation
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white">
                From Stuck to <span className="text-emerald-400">Stacked</span>
              </h2>
            </div>
          </RevealSection>

          <div className="grid md:grid-cols-2 gap-6">
            {transformations.map((t, i) => (
              <RevealSection key={i} delay={i * 0.1}>
                <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-red-400 line-through text-sm">{t.before}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <ArrowRight className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="text-white font-semibold text-lg">{t.after}</div>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="relative px-5 md:px-10 lg:px-16 py-20 md:py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="text-emerald-400 text-sm font-semibold tracking-wide uppercase mb-3 block">
                Built For Young Talent
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4">
                Everything You Need to Win
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                We're looking for hungry go-getters ready to bet on themselves.
                We'll give you everything you need to succeed — you bring the drive.
              </p>
            </div>
          </RevealSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatYouGet.map((item, i) => (
              <RevealSection key={item.title} delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -5, borderColor: "rgba(16, 185, 129, 0.5)" }}
                  className="p-6 bg-white/5 rounded-2xl border border-white/10 transition-colors h-full"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </motion.div>
              </RevealSection>
            ))}
          </div>

          <RevealSection delay={0.4}>
            <div className="mt-12 text-center">
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href={applyUrl}
                className="inline-flex items-center gap-2 bg-linear-to-r from-emerald-500 to-emerald-600 text-white text-lg font-bold px-8 py-4 rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.3)]"
              >
                I Want In <ArrowRight className="w-5 h-5" />
              </motion.a>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Social Proof / Results Section */}
      <section id="proof" className="relative px-5 md:px-10 lg:px-16 py-20 md:py-28 bg-linear-to-b from-transparent via-blue-950/20 to-transparent border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="text-blue-400 text-sm font-semibold tracking-wide uppercase mb-3 block">
                Young Winners
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4">
                They Started in Their 20s.<br />
                <span className="text-emerald-400">Now They're Stacking.</span>
              </h2>
              <p className="text-gray-500 text-lg">
                No trust fund. No degree required. No experience. Just young talent and hustle.
              </p>
            </div>
          </RevealSection>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t, i) => (
              <RevealSection key={t.name} delay={i * 0.15}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 h-full flex flex-col"
                >
                  <div className="flex items-center gap-2 text-emerald-400 font-bold text-xl mb-4">
                    <TrendingUp className="w-5 h-5" />
                    {t.income}
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                  <div className="flex items-center gap-4 pt-5 border-t border-white/10">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-white">{t.name}, {t.age}</p>
                      <p className="text-sm text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative px-5 md:px-10 lg:px-16 py-20 md:py-28 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <RevealSection>
            <div className="text-center mb-12">
              <span className="text-gray-500 text-sm font-semibold tracking-wide uppercase mb-3 block">
                Got Questions?
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white">
                Let's Clear Things Up
              </h2>
            </div>
          </RevealSection>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <RevealSection key={i} delay={i * 0.1}>
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold text-white">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="p-5 pt-0 text-gray-400">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative px-5 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/5">
        <div className="absolute inset-0 bg-linear-to-t from-emerald-950/40 to-transparent" />

        <RevealSection>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-red-400 text-sm font-semibold mb-8">
              <Flame className="w-4 h-4" />
              Hiring Young Talent — Limited Spots
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Your 20s Are For Building.<br />
              <span className="text-emerald-400">Start Now.</span>
            </h2>

            <p className="text-xl text-gray-400 mb-10 leading-relaxed">
              Same minimum wage job, same ceiling? Or build a real skill that pays you what you're worth?
              The application takes 2 minutes. The opportunity can change everything.
            </p>

            <motion.a
              whileHover={{ scale: 1.02, boxShadow: "0 0 50px rgba(16, 185, 129, 0.4)" }}
              whileTap={{ scale: 0.98 }}
              href={applyUrl}
              className="inline-flex items-center gap-3 bg-linear-to-r from-emerald-500 to-emerald-600 text-white text-xl font-bold px-10 py-5 rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.3)]"
            >
              Apply Now — It's Free <ArrowRight className="w-6 h-6" />
            </motion.a>

            <p className="text-gray-600 text-sm mt-6">
              No cost. No commitment. Just a conversation.
            </p>
          </div>
        </RevealSection>
      </section>

      {/* Footer */}
      <footer className="px-5 md:px-10 lg:px-16 py-10 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <img src={logo} alt="Empire Insurance" className="h-10" />
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">
                Terms of Use
              </a>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 space-y-4">
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, this site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc. Meta™ and the Meta™ logo are trademarks of Meta Platforms, Inc.
            </p>
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              The income figures stated ($8K-$15K/month) represent earnings of top-performing agents and are not typical or guaranteed. Your results will vary and depend on many factors including but not limited to your background, experience, work ethic, and market conditions. All business entails risk and requires consistent effort and action. This is a commission-only position with no guaranteed base salary.
            </p>
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Empire Insurance Agency. All rights reserved. 
            </p>
          </div>
        </div>
      </footer>

      {/* Spacer for mobile fixed CTA */}
      <div className="h-24 md:hidden" />
    </div>
  );
}

export default App;
